/* eslint-disable @typescript-eslint/no-magic-numbers */
import {
  TRAIL_DOT_IMAGE_ID_PREFIX,
  TRAIL_DOT_IMAGE_PIXEL_RATIO,
} from "../route-block-maplibre.constants";
import { TMapLibreImageHost } from "./maplibre-missing-style-image.models";

/** One baked dot image per fill+stroke combination (route color, or the shared detour styling). */
export const trailDotImageId = ({
  color,
  strokeColor,
  radiusPx,
  strokeWidthPx,
}: {
  color: string;
  strokeColor: string;
  radiusPx: number;
  strokeWidthPx: number;
}): string =>
  `${TRAIL_DOT_IMAGE_ID_PREFIX}${color}-${strokeColor}-${radiusPx}-${strokeWidthPx}`;

/**
 * Registers a filled circle icon so the GPS trail dots can be drawn by a `symbol` layer instead of a
 * `circle` layer. The reason is the offset: `circle-translate` is a single constant for the whole layer, so
 * circles cannot be shifted perpendicular to travel per point, while a symbol's `icon-offset` is applied in
 * the icon's rotated frame — which lets each dot ride the same offset line its trail is drawn on.
 *
 * Idempotent, and the image is intentionally never removed: the set is bounded by the route-color palette and
 * routes remount on every tracking poll, so caching is cheaper than churning add/remove. Safe to call from
 * `Map.setMissingStyleImageResolver` as well as eagerly after `style.load`.
 */
export const ensureTrailDotImage = (
  map: TMapLibreImageHost,
  {
    color,
    strokeColor,
    radiusPx,
    strokeWidthPx,
  }: {
    color: string;
    strokeColor: string;
    radiusPx: number;
    strokeWidthPx: number;
  },
): void => {
  const imageId = trailDotImageId({
    color,
    strokeColor,
    radiusPx,
    strokeWidthPx,
  });
  if (map.hasImage(imageId)) {
    return;
  }

  const ratio = TRAIL_DOT_IMAGE_PIXEL_RATIO;
  const diameter = (radiusPx + strokeWidthPx) * 2;
  const size = Math.max(1, Math.round(diameter * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return;
  }

  const center = size / 2;
  ctx.beginPath();
  ctx.arc(center, center, radiusPx * ratio, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();

  if (strokeWidthPx > 0) {
    ctx.lineWidth = strokeWidthPx * ratio;
    ctx.strokeStyle = strokeColor;
    ctx.stroke();
  }

  map.addImage(imageId, ctx.getImageData(0, 0, size, size), {
    pixelRatio: ratio,
  });
};
